/**
 * POD状态工具函数
 * 封装POD审核状态的相关逻辑和UI展示
 */
export enum PodReviewStatus {
  // 未审核
  NOT_REVIEWED = 0,
  // 机器通过
  MACHINE_APPROVED = 1,
  // 机器拒绝
  MACHINE_DISAPPROVED = 2,
  // 待人工审核
  MANUAL_PENDING = 3,
  // 人工审核通过
  MANUAL_APPROVED = 4,
  // 人工拒绝
  MANUAL_DISAPPROVED = 5
}

export interface PodStatusInfo {
  status: PodReviewStatus;
  label: string;
  color: string;
  badgeColor: string;
  buttonText: string;
  buttonTextKey: string; // 新增：翻译键
  actionable: boolean;
}

/**
 * 获取POD状态信息
 */
export const getPodStatusInfo = (status: number): PodStatusInfo => {
  switch (status) {
    case PodReviewStatus.NOT_REVIEWED:
      return {
        status: PodReviewStatus.NOT_REVIEWED,
        label: 'Not Reviewed',
        color: 'text-gray-600',
        badgeColor: 'bg-gray-950 text-gray-200',
        buttonText: 'Modify',
        buttonTextKey: 'pages.delivery.podReview.goModify',
        actionable: true
      };
    
    case PodReviewStatus.MACHINE_APPROVED:
      return {
        status: PodReviewStatus.MACHINE_APPROVED,
        label: 'Machine Approved',
        color: 'text-green-600',
        badgeColor: 'bg-green-100 text-green-600',
        buttonText: 'Modify',
        buttonTextKey: 'pages.delivery.podReview.goModify',
        actionable: true
      };
    
    case PodReviewStatus.MACHINE_DISAPPROVED:
      return {
        status: PodReviewStatus.MACHINE_DISAPPROVED,
        label: 'Machine Disapproved',
        color: 'text-red-600',
        badgeColor: 'bg-red-100 text-red-600',
        buttonText: 'Modify',
        buttonTextKey: 'pages.delivery.podReview.goModify',
        actionable: true
      };
    
    case PodReviewStatus.MANUAL_PENDING:
      return {
        status: PodReviewStatus.MANUAL_PENDING,
        label: 'Manual Pending',
        color: 'text-orange-600',
        badgeColor: 'bg-orange-100 text-orange-600',
        buttonText: 'Modify',
        buttonTextKey: 'pages.delivery.podReview.goModify',
        actionable: true
      };
    
    case PodReviewStatus.MANUAL_APPROVED:
      return {
        status: PodReviewStatus.MANUAL_APPROVED,
        label: 'Manual Approved',
        color: 'text-green-600',
        badgeColor: 'bg-green-100 text-green-600',
        buttonText: 'View',
        buttonTextKey: 'pages.delivery.podReview.view',
        actionable: true
      };
    
    case PodReviewStatus.MANUAL_DISAPPROVED:
      return {
        status: PodReviewStatus.MANUAL_DISAPPROVED,
        label: 'Manual Disapproved',
        color: 'text-red-600',
        badgeColor: 'bg-red-100 text-red-600',
        buttonText: 'Modify',
        buttonTextKey: 'pages.delivery.podReview.goModify',
        actionable: true
      };
    
    default:
      return {
        status: PodReviewStatus.NOT_REVIEWED,
        label: 'Unknown',
        color: 'text-gray-600',
        badgeColor: 'bg-gray-100 text-gray-600',
        buttonText: 'Modify',
        buttonTextKey: 'pages.delivery.podReview.goModify',
        actionable: false
      };
  }
};

/**
 * 检查POD状态是否为人工审核通过
 */
export const isManualApproved = (status: number): boolean => {
  return status === PodReviewStatus.MANUAL_APPROVED;
};

/**
 * 检查POD状态是否为不通过（机器拒绝或人工拒绝）
 */
export const isDisapproved = (status: number): boolean => {
  return status === PodReviewStatus.MACHINE_DISAPPROVED || 
         status === PodReviewStatus.MANUAL_DISAPPROVED;
};

/**
 * 检查POD状态是否为待人工审核
 */
export const isManualPending = (status: number): boolean => {
  return status === PodReviewStatus.MANUAL_PENDING;
};

/**
 * 检查是否需要显示为紧急状态（即将超时）
 * @param deliveredAt 送达时间（秒级时间戳）
 * @returns 是否为紧急状态（距离截止时间小于60分钟）
 */
export const isUrgentStatus = (deliveredAt: number | null): boolean => {
  if (!deliveredAt) return false;
  
  try {
  // deliveredAt是秒级时间戳，加24小时后转为毫秒
    const deadline = new Date((deliveredAt + 24 * 3600) * 1000);
    const now = new Date();
    
    // 计算距离截止时间的分钟数
    const minutesUntilDeadline = (deadline.getTime() - now.getTime()) / (1000 * 60);
    
    return minutesUntilDeadline < 60 && minutesUntilDeadline > 0;
  } catch (error) {
    console.error('Error calculating urgent status:', error);
    return false;
  }
};
